import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchEntryById, fetchCollection, getVal, submitWebform } from '../lib/api';

export default function MaskinDetaljer() {
  const { id } = useParams();
  const [machine, setMachine] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIdx, setActiveImageIdx] = useState(null);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [period, setLejeperiod] = useState('');
  const [message, setMessage] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    async function loadMachineData() {
      setLoading(true);
      try {
        const machineData = await fetchEntryById('maskiner', id);
        if (machineData) {
          setMachine(machineData);
          
          // Dynamisk meta titel og beskrivelse for den specifikke maskine
          const machineName = getVal(machineData.name) || 'Maskine';
          document.title = `Lej ${machineName} | Professionel maskinudlejning | J F D Service`;
          
          let metaDescription = document.querySelector('meta[name="description"]');
          if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.setAttribute('name', 'description');
            document.head.appendChild(metaDescription);
          }
          metaDescription.setAttribute('content', `Lej en professionel ${machineName} hos J F D Service. Se specifikationer, priser og send en uforpligtende forespørgsel på leje i dag.`);

          if (machineData.gallery_slug) {
            const galleryData = await fetchCollection('galleri', {
              gallery_slug: machineData.gallery_slug,
              limit: 1
            });
            if (galleryData.items && galleryData.items.length > 0) {
              setGallery(galleryData.items[0]);
            }
          }
        }
      } catch (err) {
        console.error('Fejl ved indlæsning af maskindetaljer:', err);
      } finally {
        setLoading(false);
      }
    }
    loadMachineData();
  }, [id]);

  const allImages = [];
  if (machine?.cover_image?.[0]) {
    allImages.push(machine.cover_image[0]);
  }
  if (gallery?.images) {
    gallery.images.forEach((img) => {
      if (!allImages.includes(img)) {
        allImages.push(img);
      }
    });
  }

  const openLightbox = (idx) => setActiveImageIdx(idx);
  const closeLightbox = () => setActiveImageIdx(null);
  const nextImage = (e) => {
    e.stopPropagation();
    setActiveImageIdx((prev) => (prev + 1) % allImages.length);
  };
  const prevImage = (e) => {
    e.stopPropagation();
    setActiveImageIdx((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeImageIdx === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') setActiveImageIdx((prev) => (prev + 1) % allImages.length);
      if (e.key === 'ArrowLeft') setActiveImageIdx((prev) => (prev - 1 + allImages.length) % allImages.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeImageIdx, allImages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    setSubmitError('');

    const payload = {
      name,
      phone,
      email,
      period,
      message: `Forespørgsel på leje af: ${getVal(machine?.name)}\n\nLejeperiode: ${period}\n\nBesked:\n${message}`,
      website_url: websiteUrl,
    };

    try {
      await submitWebform(payload);
      setSubmitStatus('success');
      setName('');
      setPhone('');
      setEmail('');
      setLejeperiod('');
      setMessage('');
    } catch (err) {
      console.error('Fejl ved indsendelse af forespørgsel:', err);
      setSubmitStatus('error');
      setSubmitError(err.message || 'Der skete en fejl. Prøv venligst igen eller ring til os.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-grow items-center justify-center bg-stone-50 py-32">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  if (!machine) {
    return (
      <div className="flex flex-grow flex-col items-center justify-center bg-stone-50 px-4 py-32 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-rose-50 text-rose-500">
          <i className="fa-solid fa-triangle-exclamation text-2xl" />
        </div>
        <h1 className="mb-2 text-2xl font-semibold text-slate-800">Maskinen blev ikke fundet</h1>
        <p className="mb-6 max-w-md text-slate-500">
          Den maskine, du leder efter, eksisterer ikke eller er blevet fjernet.
        </p>
        <Link
          to="/maskiner"
          className="rounded-full bg-brand-orange px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-orange-hover"
        >
          Se alle maskiner
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-stone-50">
      <div className="border-b border-stone-200 bg-white/90 py-4 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <Link to="/" className="transition-colors hover:text-brand-orange">Forside</Link>
            <i className="fa-solid fa-chevron-right text-[10px]" />
            <Link to="/maskiner" className="transition-colors hover:text-brand-orange">Udlejningsmaskiner</Link>
            <i className="fa-solid fa-chevron-right text-[10px]" />
            <span className="text-slate-700">{getVal(machine.name)}</span>
          </div>
        </div>
      </div>

      <section className="flex-grow py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            <div className="flex flex-col gap-6">
              <div
                onClick={() => openLightbox(0)}
                className="group relative aspect-video w-full cursor-pointer overflow-hidden rounded-[1.5rem] border border-stone-200 bg-white shadow-[0_12px_30px_rgba(148,163,184,0.08)]"
              >
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/${allImages[0]}`}
                  alt={getVal(machine.name)}
                  className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 bg-white/0 transition-colors group-hover:bg-white/5" />
                <div className="absolute bottom-4 right-4 rounded-full bg-slate-900/80 px-3 py-2 text-xs font-bold text-white">
                  <i className="fa-solid fa-maximize mr-2" /> Klik for stor visning
                </div>
              </div>

              {allImages.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {allImages.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() => openLightbox(idx)}
                      className="group relative aspect-video cursor-pointer overflow-hidden rounded-[1rem] border border-stone-200 bg-white"
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=80,format=auto/${img}`}
                        alt={`Galleri ${idx + 1}`}
                        className="h-full w-full object-cover object-center transition-transform duration-200 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-white/0 transition-colors group-hover:bg-white/5" />
                    </div>
                  ))}
                </div>
              )}

              {machine.specifications && (
                <div className="mt-6 rounded-[1.5rem] border border-stone-200 bg-white p-6 shadow-[0_12px_30px_rgba(148,163,184,0.08)]">
                  <h3 className="mb-4 border-b border-stone-200 pb-3 text-lg font-semibold text-slate-800">
                    <i className="fa-solid fa-list-check mr-2 text-brand-orange" /> Specifikationer
                  </h3>
                  <div
                    className="prose prose-slate max-w-none text-sm leading-relaxed text-slate-500"
                    dangerouslySetInnerHTML={{ __html: getVal(machine.specifications) }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-8">
              <div>
                <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-orange">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-orange" />
                  {machine.terms?.category?.[0] ? machine.terms.category[0].replace('-', ' ') : 'Udlejning'}
                </span>
                <h1 className="text-3xl font-semibold text-slate-800 sm:text-4xl">{getVal(machine.name)}</h1>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="text-2xl font-bold text-brand-orange">{getVal(machine.price)}</span>
                  <span className="text-sm font-medium text-slate-400">inkl. moms</span>
                </div>
                <div
                  className="mt-6 border-t border-stone-200 pt-6 text-base leading-relaxed text-slate-500"
                  dangerouslySetInnerHTML={{ __html: getVal(machine.description) }}
                />
              </div>

              <div className="rounded-[1.5rem] border border-stone-200 bg-white p-6 shadow-[0_12px_30px_rgba(148,163,184,0.08)]">
                <h3 className="mb-2 text-lg font-semibold text-slate-800">
                  <i className="fa-solid fa-calendar-plus mr-2 text-brand-orange" /> Forespørg leje
                </h3>
                <p className="mb-6 text-xs text-slate-500">
                  Udfyld formularen nedenfor, så vender vi hurtigt tilbage med bekræftelse eller tilbud.
                </p>

                {submitStatus === 'success' ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                      <i className="fa-solid fa-circle-check text-xl" />
                    </div>
                    <h4 className="mb-2 text-lg font-semibold text-slate-800">Tak for din forespørgsel!</h4>
                    <p className="text-sm text-slate-600">
                      Vi har modtaget din besked angående leje af <strong>{getVal(machine.name)}</strong>. Vi vender tilbage til dig hurtigst muligt.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="sr-only absolute overflow-hidden w-0 h-0">
                      <label htmlFor="website_url">Lad dette felt være tomt</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        autoComplete="off"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                          Navn <span className="text-brand-orange">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Dit fulde navn"
                          className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-brand-orange"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                          Telefonnummer <span className="text-brand-orange">*</span>
                        </label>
                        <input
                          type="tel"
                          required
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="Dit telefonnummer"
                          className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-brand-orange"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                          E-mail <span className="text-brand-orange">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="din@email.dk"
                          className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-brand-orange"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                          Ønsket lejeperiode <span className="text-brand-orange">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={period}
                          onChange={(e) => setLejeperiod(e.target.value)}
                          placeholder="F.eks. 3 dage, en weekend"
                          className="w-full rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-brand-orange"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                        Besked / Yderligere info <span className="text-brand-orange">*</span>
                      </label>
                      <textarea
                        required
                        rows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Skriv her hvis du har spørgsmål, ønsker levering eller har specifikke krav..."
                        className="w-full resize-none rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-brand-orange"
                      />
                    </div>

                    {submitStatus === 'error' && (
                      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-xs text-rose-700">
                        {submitError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full rounded-full bg-brand-orange py-3.5 text-center text-sm font-bold text-white transition-colors hover:bg-brand-orange-hover disabled:opacity-50"
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="fa-solid fa-spinner animate-spin" /> Sender forespørgsel...
                        </span>
                      ) : (
                        'Send forespørgsel'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {activeImageIdx !== null && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-slate-950/95 p-4"
        >
          <button
            onClick={closeLightbox}
            className="absolute right-6 top-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label="Luk visning"
          >
            <i className="fa-solid fa-xmark text-xl" />
          </button>

          {allImages.length > 1 && (
            <button
              onClick={prevImage}
              className="absolute left-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left text-lg" />
            </button>
          )}

          <div className="max-h-[85vh] max-w-[90vw] overflow-hidden rounded-[1.25rem] border border-white/10 bg-white">
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/${allImages[activeImageIdx]}`}
              alt={`Stor visning ${activeImageIdx + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {allImages.length > 1 && (
            <button
              onClick={nextImage}
              className="absolute right-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right text-lg" />
            </button>
          )}

          <div className="absolute bottom-6 text-sm font-semibold text-slate-200">
            Billede {activeImageIdx + 1} af {allImages.length}
          </div>
        </div>
      )}
    </div>
  );
}
